/**
 * Global Content Duplication Fix
 * 
 * This script addresses the issue of content appearing to duplicate under the footer
 * across all pages of the Shopify store. It works by:
 * 
 * 1. Detecting and preventing any duplicate content from appearing
 * 2. Fixing issues with overlays and high z-index elements
 * 3. Preventing third-party scripts from injecting duplicate content
 * 4. Ensuring proper scroll behavior
 * 
 * IMPORTANT: This script has been updated to work harmoniously with html-structure-fix.js
 * and to avoid interfering with jQuery's DOM operations.
 */

(function() {
  // Wait for DOM to be fully loaded
  document.addEventListener('DOMContentLoaded', function() {
    console.log('Global Content Duplication Fix: Initializing');
    
    // Flag to indicate this script is active (used for coordination with other scripts)
    window.globalContentDuplicationFixActive = true;
    
    // Store a reference to the footer
    const footer = document.querySelector('footer');
    if (!footer) {
      console.log('Global Content Duplication Fix: Could not find footer element');
      return;
    }
    
    // Store original DOM methods before overriding
    const originalAppendChild = Element.prototype.appendChild;
    const originalInsertBefore = Element.prototype.insertBefore;
    const originalRemoveChild = Node.prototype.removeChild;
    
    // ===== FIX 1: Prevent any content from being added after the footer =====
    // Create a mutation observer to watch for any elements added after the footer
    const bodyObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any nodes were added after the footer
          let currentNode = footer.nextElementSibling;
          while (currentNode) {
            const nextNode = currentNode.nextElementSibling;
            // Skip chat widgets and other essential elements
            if (!currentNode.classList.contains('shopify-chat') && 
                !currentNode.id.includes('chat') && 
                !currentNode.classList.contains('klaviyo-form-wrapper') &&
                !currentNode.classList.contains('shopify-payment-button') &&
                !currentNode.classList.contains('shopify-section')) {
              // Remove the node that was added after the footer
              try {
                if (currentNode.parentNode === mutation.target) {
                  currentNode.parentNode.removeChild(currentNode);
                  console.log('Removed node added after footer');
                }
              } catch (e) {
                console.log('Error removing node:', e);
              }
            }
            currentNode = nextNode;
          }
        }
      });
    });
    
    // Start observing the body for changes
    bodyObserver.observe(document.body, { childList: true, subtree: false });
    
    // ===== FIX 2: Clean up any existing duplicate content =====
    // Remove any existing elements after the footer
    let currentNode = footer.nextElementSibling;
    while (currentNode) {
      const nextNode = currentNode.nextElementSibling;
      // Skip chat widgets and other essential elements
      if (!currentNode.classList.contains('shopify-chat') && 
          !currentNode.id.includes('chat') && 
          !currentNode.classList.contains('klaviyo-form-wrapper') &&
          !currentNode.classList.contains('shopify-payment-button') &&
          !currentNode.classList.contains('shopify-section')) {
        // Remove the node that exists after the footer
        try {
          if (currentNode.parentNode) {
            currentNode.parentNode.removeChild(currentNode);
            console.log('Removed existing node after footer');
          }
        } catch (e) {
          console.log('Error removing existing node:', e);
        }
      }
      currentNode = nextNode;
    }
    
    // ===== FIX 3: Prevent scroll-triggered content duplication =====
    // Store a flag to prevent multiple triggers
    let isLoadingContent = false;
    let hasReachedEnd = false;
    
    // Function to safely handle scroll events with debounce
    let scrollTimer;
    window.addEventListener('scroll', function() {
      // Clear any existing timeout
      clearTimeout(scrollTimer);
      
      // Set a new timeout
      scrollTimer = setTimeout(function() {
        // If we're already loading or have reached the end, don't trigger again
        if (isLoadingContent || hasReachedEnd) return;
        
        const scrollPosition = window.scrollY + window.innerHeight;
        const footerPosition = footer.offsetTop;
        
        // If we're approaching the footer
        if (scrollPosition > footerPosition - 200) {
          isLoadingContent = true;
          
          // Check for any duplicate content that might have been added
          const contentElements = document.querySelectorAll('.collection-description, .collection__description, .rte');
          const contentTexts = {};
          
          contentElements.forEach(function(el) {
            const text = el.textContent.trim();
            if (text.length > 50) {
              if (contentTexts[text]) {
                // Found duplicate content, remove it
                try {
                  if (el.parentNode) {
                    el.parentNode.removeChild(el);
                    console.log('Removed duplicate content element');
                  }
                } catch (e) {
                  console.log('Error removing duplicate content:', e);
                }
              } else {
                contentTexts[text] = true;
              }
            }
          });
          
          // Mark that we've reached the end to prevent further loading
          hasReachedEnd = true;
          
          // Reset loading flag after a delay
          setTimeout(function() {
            isLoadingContent = false;
          }, 500);
        }
      }, 100);
    });
    
    // ===== FIX 4: Fix overlay and high z-index issues =====
    // Find any overlays or fixed position elements that might appear as duplicated content
    const overlays = document.querySelectorAll('.overlay, .modal, .popup, [style*="position: fixed"], [style*="position:fixed"], [class*="overlay"], [class*="modal"], [class*="popup"]');
    
    overlays.forEach(function(overlay) {
      // Ensure overlays don't extend beyond the footer
      const style = window.getComputedStyle(overlay);
      if (style.position === 'fixed' && style.bottom === '0px') {
        // Adjust the overlay to not overlap with footer content
        overlay.style.maxHeight = 'calc(100vh - 100px)';
        overlay.style.overflow = 'auto';
      }
    });
    
    // ===== FIX 5: Safe DOM method overrides that preserve jQuery compatibility =====
    
    // Create a function to check if the caller is jQuery
    function isJQueryCall() {
      try {
        const stack = new Error().stack || '';
        return stack.indexOf('jquery') !== -1 || stack.indexOf('jQuery') !== -1;
      } catch (e) {
        return false;
      }
    }
    
    // Override appendChild with jQuery compatibility
    Element.prototype.appendChild = function(child) {
      // If this is a jQuery call or we're in jQuery initialization, use original method
      if (isJQueryCall() || window.jQueryInitializing || typeof jQuery !== 'undefined' && jQuery.isReady === false) {
        return originalAppendChild.call(this, child);
      }
      
      // If we're appending to the body and the footer exists
      if (this === document.body && footer && child.nodeType === 1) {
        // Check if the footer is already in the DOM
        if (document.body.contains(footer)) {
          try {
            // Insert before the footer instead of at the end
            return this.insertBefore(child, footer.nextSibling);
          } catch (e) {
            console.log('Error in appendChild override:', e);
            // Fall back to original method
            return originalAppendChild.call(this, child);
          }
        }
      }
      return originalAppendChild.call(this, child);
    };
    
    // Override insertBefore with jQuery compatibility
    Element.prototype.insertBefore = function(newNode, referenceNode) {
      // If this is a jQuery call or we're in jQuery initialization, use original method
      if (isJQueryCall() || window.jQueryInitializing || typeof jQuery !== 'undefined' && jQuery.isReady === false) {
        return originalInsertBefore.call(this, newNode, referenceNode);
      }
      
      // If we're inserting into the body and after the footer
      if (this === document.body && footer && newNode.nodeType === 1) {
        try {
          // Check if we're trying to insert after the footer
          let currentNode = footer;
          while (currentNode) {
            if (currentNode === referenceNode) {
              // We're trying to insert after the footer, prevent this
              // unless it's a critical element
              if (!newNode.classList.contains('shopify-chat') && 
                  !newNode.id.includes('chat') && 
                  !newNode.classList.contains('klaviyo-form-wrapper') &&
                  !newNode.classList.contains('shopify-payment-button') &&
                  !newNode.classList.contains('shopify-section')) {
                console.log('Prevented insertion after footer');
                return newNode; // Return the node without inserting it
              }
              break;
            }
            currentNode = currentNode.nextSibling;
          }
        } catch (e) {
          console.log('Error in insertBefore override:', e);
          // Fall back to original method
          return originalInsertBefore.call(this, newNode, referenceNode);
        }
      }
      return originalInsertBefore.call(this, newNode, referenceNode);
    };
    
    // Override removeChild with jQuery compatibility
    Node.prototype.removeChild = function(child) {
      // If this is a jQuery call or we're in jQuery initialization, use original method
      if (isJQueryCall() || window.jQueryInitializing || typeof jQuery !== 'undefined' && jQuery.isReady === false) {
        return originalRemoveChild.call(this, child);
      }
      
      try {
        // Check if child is actually a child of this node
        if (!this.contains(child)) {
          console.log('Prevented removeChild on non-child node');
          return child; // Return the node without removing it
        }
        return originalRemoveChild.call(this, child);
      } catch (e) {
        console.log('Error in removeChild override:', e);
        // Fall back to original behavior
        return originalRemoveChild.call(this, child);
      }
    };
    
    // ===== FIX 6: Ensure jQuery plugins work correctly =====
    // Add a flag to indicate when jQuery is initializing
    if (typeof jQuery !== 'undefined') {
      const originalReady = jQuery.fn.ready;
      jQuery.fn.ready = function(fn) {
        window.jQueryInitializing = true;
        const result = originalReady.apply(this, arguments);
        setTimeout(function() {
          window.jQueryInitializing = false;
        }, 0);
        return result;
      };
      
      // Ensure pjax plugin is properly initialized
      if (jQuery.fn.pjax === undefined && typeof $.fn.pjax !== 'undefined') {
        jQuery.fn.pjax = $.fn.pjax;
      }
    }
    
    console.log('Global Content Duplication Fix: Initialization complete');
  });
})();
